import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { OPCIONES_BASE, EJES_BASE, agruparEnOtros, datasetBarrasSimple, datasetLinea } from '../../utils/graficos';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
// Servicio que llama al backend
import * as reportesGastosService from '../../services/reportes/reportesGastosService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageHeader from '../../components/ui/PageHeader';
import '../../style/pages/HomePage.css';



const formatFechaISOaTexto = (isoString) => {
    if (!isoString) return '';
    const partes = isoString.split('-');
    if (partes.length !== 3) return '';
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};



const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const BASE_OPTIONS = OPCIONES_BASE;
// Barras de una sola medida: sin leyenda y ejes del sistema.
const OPCIONES_BARRAS = { ...OPCIONES_BASE, plugins: { ...OPCIONES_BASE.plugins, legend: { display: false } }, scales: EJES_BASE };
const OPCIONES_LINEA = { ...OPCIONES_BASE, scales: EJES_BASE, interaction: { mode: 'index', intersect: false } };



// Helpers

const fmtARS = (v) => parseFloat(v||0).toLocaleString('es-AR', { style:'currency', currency:'ARS' });



const fmtFechaMientrasEscribe = (v) => {

    const n = v.replace(/\D/g,'');

    if (!n.length) return '';

    if (n.length<=2) return n;

    if (n.length<=4) return `${n.slice(0,2)}/${n.slice(2)}`;

    return `${n.slice(0,2)}/${n.slice(2,4)}/${n.slice(4,8)}`;

};



const textoAISO = (texto) => {

    if (!texto?.trim()) return null;

    const p = texto.replace(/[^\d/]/g,'').split('/');

    if (p.length===3) {

        const [d,m,a] = p.map(Number);

        if (d>0&&d<=31&&m>0&&m<=12&&a>=1900&&a<=2100) {

            const f = new Date(a,m-1,d);

            if (!isNaN(f.getTime())&&f.getDate()===d&&f.getMonth()===m-1)

                return f.toISOString().split('T')[0];

        }

    }

    return null;

};



function ReportesGastos() {

    const navigate = useNavigate();



    // Estados para datos del backend

    const [resumen, setResumen] = useState(null);

    const [porTipo, setPorTipo] = useState([]);

    const [porMes, setPorMes] = useState([]);

    const [porMedico, setPorMedico] = useState([]);

    const [detalle, setDetalle] = useState([]);

    const [loading, setLoading] = useState(true);



    // Filtros

    // Start with 12 months as requested

    const defaultPeriod = "12m";

    const getFechasFromPeriodo = (period) => {

        const hoy = new Date();

        if (period === "ytd") {

            const startDate = new Date(hoy.getFullYear(), 0, 1).toISOString().split('T')[0];

            return { inicio: startDate, fin: hoy.toISOString().split('T')[0] };

        } else if (period === "12m") {

            const startDate = new Date();

            startDate.setFullYear(hoy.getFullYear() - 1);

            return { inicio: startDate.toISOString().split('T')[0], fin: hoy.toISOString().split('T')[0] };

        }

        return { inicio: '', fin: '' };

    };



    const initialFechas = getFechasFromPeriodo(defaultPeriod);

    const [periodo, setPeriodo] = useState(defaultPeriod);

    const [filtroFechaInicio, setFiltroFechaInicio] = useState(initialFechas.inicio);

    const [filtroFechaFin, setFiltroFechaFin] = useState(initialFechas.fin);

    const [filtroFechaInicioTexto, setFiltroFechaInicioTexto] = useState(formatFechaISOaTexto(initialFechas.inicio));

    const [filtroFechaFinTexto, setFiltroFechaFinTexto] = useState(formatFechaISOaTexto(initialFechas.fin));

    const [filtroMedico, setFiltroMedico] = useState('');

    const [filtroTipoTramite, setFiltroTipoTramite] = useState('');



    const handlePeriodoChange = (e) => {

        const nuevoPeriodo = e.target.value;

        setPeriodo(nuevoPeriodo);

        if (nuevoPeriodo === 'custom') {

            setFiltroFechaInicio('');

            setFiltroFechaFin('');

            setFiltroFechaInicioTexto('');

            setFiltroFechaFinTexto('');

        } else {

            const fechas = getFechasFromPeriodo(nuevoPeriodo);

            setFiltroFechaInicio(fechas.inicio);

            setFiltroFechaFin(fechas.fin);

            setFiltroFechaInicioTexto(formatFechaISOaTexto(fechas.inicio));

            setFiltroFechaFinTexto(formatFechaISOaTexto(fechas.fin));

        }

    };



    // Refs para PDF

    const refPieTipo = useRef(null);

    const refBarTipo = useRef(null);

    const refEvolucion = useRef(null);

    const refPorMedico = useRef(null);



    // Cargar datos del backend

    useEffect(() => {

        const cargarReportes = async () => {

            try {

                setLoading(true);



                // Validar y ordenar fechas

                let fechaDesdeAjustada = filtroFechaInicio;

                let fechaHastaAjustada = filtroFechaFin;



                if (fechaDesdeAjustada && fechaHastaAjustada && fechaDesdeAjustada > fechaHastaAjustada) {

                    console.warn('⚠️ Fechas invertidas, intercambiando...');

                    [fechaDesdeAjustada, fechaHastaAjustada] = [fechaHastaAjustada, fechaDesdeAjustada];

                    setFiltroFechaInicio(fechaDesdeAjustada);

                    setFiltroFechaFin(fechaHastaAjustada);

                }



                console.log('📊 Cargando reportes gastos con filtros:', {

                    fechaInicio: fechaDesdeAjustada,

                    fechaFin: fechaHastaAjustada,

                    idMedico: filtroMedico,

                    idTipoTramite: filtroTipoTramite,

                });



                const filtros = {

                    fechaDesde: fechaDesdeAjustada || null,

                    fechaHasta: fechaHastaAjustada || null,

                    idMedico: filtroMedico ? parseInt(filtroMedico) : null,

                    idTipoTramite: filtroTipoTramite ? parseInt(filtroTipoTramite) : null,

                };



                console.log('📤 Enviando filtros al backend:', filtros);



                const datos = await reportesGastosService.getAllReportes(filtros);



                console.log('✅ Datos recibidos:', datos);



                if (datos && datos.resumen && datos.porTipo && datos.porMes && datos.porMedico && datos.detalle) {

                    setResumen(datos.resumen);

                    setPorTipo(datos.porTipo);

                    setPorMes(datos.porMes);

                    setPorMedico(datos.porMedico);

                    setDetalle(datos.detalle);

                    console.log('✓ Estados actualizados correctamente');

                } else {

                    console.warn('⚠️ Estructura incompleta:', datos);

                }

            } catch (err) {

                console.error('❌ Error:', err);

                console.error('Detalles:', err.response?.data || err.message);

            } finally {

                setLoading(false);

            }

        };

        cargarReportes();

    }, [filtroFechaInicio, filtroFechaFin, filtroMedico, filtroTipoTramite]);



    // Gráficos - datos ya vienen procesados del backend

    const pieTipoData = useMemo(() => {
        // Paleta del sistema: máximo 6 porciones, el resto en "Otros".
        const g = agruparEnOtros(porTipo.map(e => e.nombreTipo), porTipo.map(e => e.totalMonto));
        return { labels: g.labels, datasets: [{ data: g.data, backgroundColor: g.colores }] };
    }, [porTipo]);



    const barTipoData = useMemo(() => ({

        labels: porTipo.map(e => e.nombreTipo),

        datasets: [datasetBarrasSimple('Gasto total', porTipo.map(e => e.totalMonto))]

    }), [porTipo]);



    const datosEvolucion = useMemo(() => ({

        labels: porMes.map(m => `${MESES[m.mes - 1]} ${m.anio}`),

        datasets: [

            datasetLinea('Total', porMes.map(m => m.totalGeneral), 0),

            datasetLinea('Médicos', porMes.map(m => m.totalMedicos), 1),

            datasetLinea('No médicos', porMes.map(m => m.totalNoMedicos), 2),

        ],

    }), [porMes]);



    const dataPorMedico = useMemo(() => ({

        labels: porMedico.map(m => m.nombreMedico),

        datasets: [datasetBarrasSimple('Gasto total', porMedico.map(m => m.totalMonto))]

    }), [porMedico]);



    const limpiarFiltros = () => {

        setPeriodo('12m');

        const defaultFechas = getFechasFromPeriodo('12m');

        setFiltroFechaInicio(defaultFechas.inicio);

        setFiltroFechaFin(defaultFechas.fin);

        setFiltroFechaInicioTexto(formatFechaISOaTexto(defaultFechas.inicio));

        setFiltroFechaFinTexto(formatFechaISOaTexto(defaultFechas.fin));

        setFiltroMedico('');

        setFiltroTipoTramite('');

    };



    const generarPDF = () => {
        const doc = new jsPDF();
        const pageW = doc.internal.pageSize.width;
        const m = 14;
        let y = 20;

        // Función auxiliar para extraer gráfico en alta calidad
        const exportarGrafico = (chartRef, titulo) => {
            if (chartRef.current && chartRef.current.canvas) {
                const canvas = chartRef.current.canvas;
                const imgData = canvas.toDataURL('image/png', 1.0);

                // Crear nueva página
                doc.addPage();
                y = 20;

                // Título
                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(46, 80, 144);
                doc.text(titulo, m, y);
                y += 15;

                // Calcular proporciones manteniendo aspecto
                const ratio = canvas.height / canvas.width;
                const ancho = pageW - (m * 2);
                const alto = ancho * ratio;

                // Agregar imagen centrada
                const xCentro = (pageW - ancho) / 2;
                doc.addImage(imgData, 'PNG', xCentro, y, ancho, alto);
            }
        };

        // --- PÁGINA 1: PORTADA Y RESUMEN ---
        doc.setFillColor(240, 253, 250);
        doc.rect(0, 0, pageW, 50, 'F');
        doc.setFontSize(22);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(46, 80, 144);
        doc.text('Reporte de Gastos', pageW / 2, 28, { align: 'center' });

        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Generado el ${new Date().toLocaleDateString('es-ES')}`, pageW / 2, 38, { align: 'center' });

        y = 60;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('RESUMEN EJECUTIVO', m, y);
        y += 12;

        if (resumen) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            const resumenInfo = [
                `Total gastado: ${fmtARS(resumen.totalGastado)}`,
                `Cantidad de gastos: ${resumen.cantidadGastos}`,
                `Gasto promedio: ${fmtARS(resumen.gastoPromedio)}`,
                `Gastos médicos: ${fmtARS(resumen.totalMedicos)} (${resumen.pctMedicos}%)`,
                `Gastos no médicos: ${fmtARS(resumen.totalNoMedicos)} (${resumen.pctNoMedicos}%)`
            ];
            resumenInfo.forEach(linea => {
                doc.text(linea, m, y);
                y += 7;
            });
        }

        // --- PÁGINA 2: GRÁFICO PIE (Distribución por Tipo) ---
        exportarGrafico(refPieTipo, 'Distribución por Tipo de Gasto');

        // --- PÁGINA 3: GRÁFICO BAR (Montos por Categoría) ---
        exportarGrafico(refBarTipo, 'Montos por Categoría');

        // --- PÁGINA 4: GRÁFICO EVOLUCIÓN (Evolución Mensual) ---
        exportarGrafico(refEvolucion, 'Evolución Mensual de Gastos');

        // --- PÁGINA 5: GRÁFICO POR MÉDICO ---
        exportarGrafico(refPorMedico, 'Gastos por Médico');

        // --- PÁGINA 6: TABLA DETALLE ---
        if (detalle.length > 0) {
            doc.addPage();
            y = 20;
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(46, 80, 144);
            doc.text('DETALLE COMPLETO', m, y);
            y += 10;

            autoTable(doc, {
                head: [['Fecha', 'Tipo', 'Médico', 'Monto', 'Comentario']],
                body: detalle.map(g => [
                    g.fecha,
                    g.nombreTipo,
                    g.nombreMedico || '—',
                    fmtARS(g.monto),
                    g.comentario || '—',
                ]),
                startY: y,
                headStyles: { fillColor: [46, 80, 144] },
                styles: { fontSize: 9 },
                margin: { left: m, right: m }
            });
        }

        doc.save(`reporte_gastos_${new Date().toISOString().split('T')[0]}.pdf`);
    };



    if (loading) return (

        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>

            <Spinner animation="border" style={{ color: '#10B981' }} />

            <span className="ms-3">Cargando reporte de gastos desde el servidor...</span>

        </div>

    );



    const hayDatos = porTipo && porTipo.length > 0;



    return (

        <div className="homepage-container area-reportes">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <span role="button" style={{ cursor: 'pointer', color: '#3B82F6', textDecoration: 'underline' }} onClick={() => navigate('/')}>
                            Inicio
                        </span>
                    </li>
                    <li className="breadcrumb-item active">Reportes de Gastos</li>
                </ol>
            </nav>

            <PageHeader
                variante="plano"
                icono="bar-chart-fill"
                titulo="Reportes de Gastos"
                descripcion="Análisis centralizado de gastos médicos y no médicos"
                accion={
                    <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={generarPDF}
                    >
                        <i className="bi bi-file-earmark-pdf" />
                        Exportar PDF
                    </button>
                }
            />



            {/* KPIs Cards */}

            <div className="ds-kpis">
                <TarjetaKpi label="Total gastado" valor={resumen ? fmtARS(resumen.totalGastado) : '—'} icono="cash-stack" />
                <TarjetaKpi label="Cantidad de gastos" valor={resumen ? resumen.cantidadGastos : 0} icono="receipt" />
                <TarjetaKpi label="Gasto promedio" valor={resumen ? fmtARS(resumen.gastoPromedio) : '—'} icono="calculator" />
            </div>



            {/* Filtros */}

            <div className="eventos-section" style={{ marginBottom: '2rem', marginTop: '2rem' }}>

                <div className="eventos-header-row">

                    <h2 className="eventos-section-title"><i className="bi bi-funnel me-2" />Filtros</h2>

                    <button type="button" className="btn-ver-calendario-home" onClick={limpiarFiltros}>

                        <i className="bi bi-x-circle me-2" />Limpiar filtros

                    </button>

                </div>

                <Row className="g-3 align-items-end">

                    <Col md={4}>

                        <Form.Group>

                            <Form.Label>Período</Form.Label>

                            <Form.Select

                                value={periodo}

                                onChange={handlePeriodoChange}

                                

                            >

                                <option value="12m">Últimos 12 meses</option>

                                <option value="ytd">Año actual</option>

                                <option value="custom">Personalizado</option>

                            </Form.Select>

                        </Form.Group>

                    </Col>

                    <Col md={4}>

                        <Form.Group>

                            <Form.Label>Fecha Desde</Form.Label>

                            <div className="d-flex gap-2">

                                <Form.Control

                                    type="text"

                                    placeholder="dd/mm/aaaa"

                                    value={filtroFechaInicioTexto}

                                    disabled={periodo !== 'custom'}

                                    onChange={e => {

                                        const inputValue = e.target.value;

                                        const fmt = fmtFechaMientrasEscribe(inputValue);

                                        setFiltroFechaInicioTexto(fmt);



                                        const iso = textoAISO(fmt);

                                        if (iso) {

                                            setFiltroFechaInicio(iso);

                                        } else if (!inputValue) {

                                            setFiltroFechaInicio('');

                                        }

                                    }}

                                    onBlur={e => {

                                        if (e.target.value && !textoAISO(e.target.value)) {

                                            setFiltroFechaInicioTexto('');

                                            setFiltroFechaInicio('');

                                        }

                                    }}

                                    style={{ flex: 1, backgroundColor: periodo !== 'custom' ? '#e9ecef' : 'white' }}

                                />

                                <Form.Control

                                    type="date"

                                    value={filtroFechaInicio}

                                    disabled={periodo !== 'custom'}

                                    onChange={e => {

                                        setFiltroFechaInicio(e.target.value);

                                        setFiltroFechaInicioTexto(formatFechaISOaTexto(e.target.value));

                                    }}

                                    style={{ width: '45px', backgroundColor: periodo !== 'custom' ? '#e9ecef' : 'white' }}

                                />

                            </div>

                        </Form.Group>

                    </Col>

                    <Col md={4}>

                        <Form.Group>

                            <Form.Label>Fecha Hasta</Form.Label>

                            <div className="d-flex gap-2">

                                <Form.Control

                                    type="text"

                                    placeholder="dd/mm/aaaa"

                                    value={filtroFechaFinTexto}

                                    disabled={periodo !== 'custom'}

                                    onChange={e => {

                                        const inputValue = e.target.value;

                                        const fmt = fmtFechaMientrasEscribe(inputValue);

                                        setFiltroFechaFinTexto(fmt);



                                        const iso = textoAISO(fmt);

                                        if (iso) {

                                            setFiltroFechaFin(iso);

                                        } else if (!inputValue) {

                                            setFiltroFechaFin('');
                                        }
                                    }}
                                    onBlur={e => {
                                        if (e.target.value && !textoAISO(e.target.value)) {
                                            setFiltroFechaFinTexto('');
                                            setFiltroFechaFin('');
                                        }
                                    }}
                                    style={{ flex: 1, backgroundColor: periodo !== 'custom' ? '#e9ecef' : 'white' }}
                                />
                                <Form.Control
                                    type="date"
                                    value={filtroFechaFin}
                                    disabled={periodo !== 'custom'}
                                    onChange={e => {
                                        setFiltroFechaFin(e.target.value);
                                        setFiltroFechaFinTexto(formatFechaISOaTexto(e.target.value));
                                    }}
                                    style={{ width: '45px', backgroundColor: periodo !== 'custom' ? '#e9ecef' : 'white' }}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            {/* Gráficos */}
            {hayDatos ? (
                <>
                    <Row className="g-4 mb-4">
                        <Col lg={6}>
                            <div className="eventos-section">
                                <h2 className="eventos-section-title">Distribución por tipo de gasto</h2>
                                <p className="text-muted small mb-3">Proporción del gasto total según su tipo, en el período filtrado.</p>
                                <Pie ref={refPieTipo} data={pieTipoData} options={BASE_OPTIONS} />
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="eventos-section">
                                <h2 className="eventos-section-title">Montos por categoría</h2>
                                <p className="text-muted small mb-3">Mismo desglose que el gráfico anterior, en barras para comparar montos entre categorías.</p>
                                <Bar ref={refBarTipo} data={barTipoData} options={OPCIONES_BARRAS} />
                            </div>
                        </Col>
                    </Row>

                    <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                        <h2 className="eventos-section-title">Evolución mensual de gastos</h2>
                        <p className="text-muted small mb-3">Gasto total mes a mes, desglosado en médicos y no médicos.</p>
                        <Line ref={refEvolucion} data={datosEvolucion} options={OPCIONES_LINEA} />
                    </div>

                    <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                        <h2 className="eventos-section-title">Gastos por médico</h2>
                        <p className="text-muted small mb-3">Monto total gastado por cada médico en el período filtrado.</p>
                        <Bar ref={refPorMedico} data={dataPorMedico} options={OPCIONES_BARRAS} />
                    </div>

                    {/* Tabla Detalle */}
                    <div className="eventos-section">
                        <h2 className="eventos-section-title">Detalle de gastos</h2>
                        <p className="text-muted small mb-3">Listado completo de gastos del período, con fecha, tipo, médico y monto.</p>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th>Fecha</th>
                                    <th>Tipo</th>
                                    <th>Médico</th>
                                    <th className="text-end">Monto</th>
                                    <th>Comentario</th>
                                </tr>
                                </thead>
                                <tbody>
                                {detalle.map((g, idx) => (
                                    <tr key={idx}>
                                        <td>{g.fecha}</td>
                                        <td>{g.nombreTipo}</td>
                                        <td>{g.nombreMedico || '—'}</td>
                                        <td className="text-end fw-bold">{fmtARS(g.monto)}</td>
                                        <td>{g.comentario || '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2" />No hay datos disponibles con los filtros seleccionados.
                </div>
            )}
        </div>
    );
}

export default ReportesGastos;

